// Таблица записей смарт-процесса. Колонки приходят снаружи — их состав вычислен по
// справочнику полей (lib/smart.ts), а не написан словами: у пяти типов поля разные и
// меняются в Bitrix без нас.
//
// Один компонент на все типы намеренно: правила показа (как выглядит стадия, чем помечено
// поле, которое пишет бизнес-процесс) должны совпадать во всех разделах. Разъедутся правила —
// разъедутся и цифры, ровно как разъехались они у порталов 55 и exec.

import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useCompanyTitles, useFields, useItem, useRefs } from '../api/hooks'
import type { CrmItem } from '../api/types'
import { fmtText } from '../lib/format'
import type { SmartColumn } from '../lib/smart'
import {
  bitrixItemUrl, cellText, detailKind, isNumCol, parseCrmRef, refId, smartEntity,
} from '../lib/smart'
import { EntityLink } from './EntityLink'

interface Props {
  typeId: number
  rows: CrmItem[]
  cols: SmartColumn[]
  emptyText?: string
  /**
   * Ядро пометило выборку устаревшей (`data.stale`). Тогда пустая таблица — не «записей
   * нет», и `emptyText` печатать нельзя: см. ветку ниже. Проп такой же, как у PaymentsTable,
   * и по той же причине.
   */
  stale?: boolean
}

export function SmartItemsTable({
  typeId, rows, cols, emptyText = 'Записей нет', stale = false,
}: Props) {
  const refs = useRefs()
  const f = useFields(smartEntity(typeId))
  const [open, setOpen] = useState('')   // раскрытая строка: id записи

  // названия компаний — одним запросом на всю страницу (иначе 50 строк = 50 запросов)
  const companyIds = useMemo(
    () => (cols.some((c) => c.kind === 'company') ? rows.map((r) => refId(r.companyId)) : []),
    [rows, cols],
  )
  const titles = useCompanyTitles(companyIds)

  // Пустая таблица на устаревших данных — это НЕ «записей нет». Пометку «синк отстал» ставит
  // QueryState выше, но без этой ветки прямо под ней печаталось бы «Счетов за клиентом нет»:
  // два взаимоисключающих утверждения, из которых человек выбирает привычное — «счетов нет».
  // Строки, если они пришли, показываем как есть: врёт не таблица, а вывод об отсутствии.
  if (rows.length === 0) {
    // Общий блок пустого состояния (styles.css §8), как в PaymentsTable и по той же причине:
    // «записей нет» — это ответ, и он должен читаться как ответ, а не как недогрузившаяся
    // таблица. Слова не меняются, меняется только их оформление.
    return (
      <div className="empty">
        <div className="empty-title">
          {stale
            ? 'Записи не показаны: ядро предупредило, что данные устарели. Пустой список здесь'
              + ' не означает «записей нет» — правильного ответа у нас сейчас нет.'
            : emptyText}
        </div>
      </div>
    )
  }

  const cell = (col: SmartColumn, r: CrmItem) => {
    const v = r[col.code]
    switch (col.kind) {
      case 'id':
        // У договоров (153) с 31.07.2026 есть СВОЯ карточка — ведём в неё, а не в Bitrix.
        if (typeId === 153 && !String(r.id).startsWith('own-')) {
          return <Link to={`/smart/contracts/${String(r.id)}`}>{String(r.id)}</Link>
        }
        // «own-15» — запись, заведённая уже в своей схеме (без следа Bitrix): такой id
        // приходит из own-источника (api/smartOwn.ts, приём docs_api._ид_наружу).
        // Ссылка в Bitrix вела бы на несуществующую карточку — показываем номер текстом.
        if (String(r.id).startsWith('own-')) {
          return (
            <span className="mono"
                  title="Запись заведена уже в своей схеме — карточки в Bitrix у неё нет">
              {String(r.id)}
            </span>
          )
        }
        return (
          <a href={bitrixItemUrl(typeId, String(r.id))} target="_blank" rel="noreferrer"
             title="Открыть запись в Bitrix (своей карточки в CRM пока нет)">
            {String(r.id)}
          </a>
        )
      case 'stage':
        return (
          <span className={`badge ${refs.itemStageKind(String(v ?? ''))}`}>
            {refs.itemStageName(String(v ?? ''))}
          </span>
        )
      case 'category':
        return <span className="muted">{refs.itemCategoryName(typeId, v as string)}</span>
      case 'company':
        return <EntityLink kind="company" id={refId(v)} title={titles.data?.[refId(v)] || null} />
      case 'contact':
        return <EntityLink kind="contact" id={refId(v)} />
      case 'deal':
        return <EntityLink kind="deal" id={refId(v)} />
      case 'user':
        return refId(v) ? refs.userName(refId(v)) : <span className="muted">—</span>
      case 'crm': {
        const ref = parseCrmRef(Array.isArray(v) ? v[0] : v)
        return ref ? <EntityLink kind={ref.kind} id={ref.id} /> : cellText(col, v, f)
      }
      default:
        return cellText(col, v, f)
    }
  }

  const tdClass = (kind: SmartColumn['kind']) => [
    isNumCol(kind) ? 'num' : '',
    kind === 'id' ? 'mono nowrap' : '',
    kind === 'date' || kind === 'datetime' || kind === 'user' ? 'nowrap' : '',
  ].filter(Boolean).join(' ')

  return (
    // `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). Подпись
    // ячейки берётся из того же `c.label`, что и заголовок колонки: колонки здесь считаются
    // по справочнику полей Bitrix, и вторая копия подписи разъехалась бы при первом же
    // переименовании поля.
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th style={{ width: 24 }} />
            {/* Числовая колонка — классом `num`, а не инлайновым выравниванием: `.num`
                даёт и выравнивание вправо, и `nowrap`, и табличные цифры (styles.css §2
                и §5). Ячейки под этой шапкой уже считают класс тем же признаком (`tdClass`
                выше) — шапка теперь считает его так же, а не своим способом. */}
            {cols.map((c) => (
              <th key={c.code} className={isNumCol(c.kind) ? 'num' : 'nowrap'}
                  title={[
                    c.code,
                    c.known ? '' : 'название поля в справочнике ядра не заполнено',
                    c.bpComputed ? 'значение считает бизнес-процесс Bitrix' : '',
                  ].filter(Boolean).join(' · ')}>
                {c.label}
                {c.bpComputed && <span className="muted" title="считает бизнес-процесс"> ⚙</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const id = String(r.id)
            return (
              <Fragment key={id}>
                <tr>
                  {/* У колонки нет названия и в шапке, поэтому подпись пустая, а не забытая. */}
                  <td data-label="">
                    {/* В списке видно 5–8 полей из сорока. Раскрытие читает запись целиком
                        (crm.item.get) — по требованию, а не для всех пятидесяти строк сразу. */}
                    <button type="button" onClick={() => setOpen(open === id ? '' : id)}
                            title={open === id ? 'Свернуть' : 'Показать все поля записи'}
                            style={{ padding: '2px 7px', lineHeight: 1 }}>
                      {open === id ? '−' : '+'}
                    </button>
                  </td>
                  {cols.map((c) => (
                    <td key={c.code} className={tdClass(c.kind)} data-label={c.label}>{cell(c, r)}</td>
                  ))}
                </tr>
                {open === id && (
                  <tr>
                    <td data-label="" />
                    <td colSpan={cols.length}>
                      {/* Полная запись читается из зеркала (crm.item.get). У записи,
                          заведённой уже в своей схеме, зеркального двойника нет — и
                          спрашивать его значило бы показать «Запись не найдена» там,
                          где верный ответ «записи в Bitrix никогда не было». */}
                      {id.startsWith('own-')
                        ? (
                          <div className="hint" style={{ padding: '6px 0 10px' }}>
                            Запись заведена уже в своей схеме, в Bitrix её никогда не было.
                            Все поля Bitrix показать нечем — в списке видно всё, что о ней
                            перенесено и заполнено.
                          </div>
                        )
                        : <SmartItemFields typeId={typeId} id={id} />}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Все поля одной записи. Названия — из справочника; поля, которых в справочнике нет,
 * показываем кодом: это не «неизвестное значение», а «Bitrix не описал поле».
 */
function SmartItemFields({ typeId, id }: { typeId: number; id: string }) {
  const f = useFields(smartEntity(typeId))
  const q = useItem(typeId, id)
  const item = q.data

  const shown = useMemo(() => {
    if (!item) return []
    const out: Array<{ code: string; label: string; text: string; bp: boolean }> = []
    const seen = new Set<string>()
    const push = (code: string) => {
      if (seen.has(code)) return
      seen.add(code)
      const v = item[code]
      if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return
      const meta = f.meta(code)
      const known = !!(meta?.title && meta.title !== code)
      const col: SmartColumn = {
        code,
        kind: detailKind(code, meta?.type),
        label: known ? meta!.title : code,
        known,
        multiple: !!meta?.multiple,
        bpComputed: !!meta?.bp_computed,
        system: false,
      }
      out.push({ code, label: col.label, text: cellText(col, v, f), bp: col.bpComputed })
    }
    // порядок Bitrix: сначала описанные в справочнике поля, потом всё остальное из записи
    for (const { code } of f.list) push(code)
    for (const code of Object.keys(item)) push(code)
    return out
  }, [item, f])

  if (q.isLoading) return <div className="skeleton" style={{ width: '60%' }} />
  if (q.error) {
    return (
      <div className="error">
        Запись целиком прочитать не удалось: {(q.error as Error).message}
      </div>
    )
  }

  const emptyCount = item ? Object.keys(item).length - shown.length : 0

  return (
    <div style={{ padding: '6px 0 10px' }}>
      <dl className="dl">
        {shown.map((s) => (
          <div key={s.code} style={{ display: 'contents' }}>
            <dt title={s.code}>
              {s.label}
              {s.bp && <span className="muted" title="значение считает бизнес-процесс"> ⚙</span>}
            </dt>
            <dd>{fmtText(s.text)}</dd>
          </div>
        ))}
      </dl>
      <div className="hint" style={{ marginTop: 8 }}>
        Показаны заполненные поля{emptyCount > 0 ? `; пустых — ${emptyCount}` : ''}. Значок ⚙ —
        значение пишет бизнес-процесс Bitrix.
        {!f.loaded && ' Справочник полей ядра недоступен: названия показаны кодами Bitrix.'}
      </div>
    </div>
  )
}
